import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  clearToken,
  clearShopperToken,
  clearVeyaTeamToken,
  getShopperToken,
  getToken,
  getVeyaTeamToken,
} from "../lib/api";
import { getCurrentUser } from "../lib/authApi";
import { getCurrentShopper } from "../lib/shopperAuthApi";

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [merchant, setMerchant] = useState(null);
  const [shopper, setShopper] = useState(null);
  const [veyaTeam, setVeyaTeam] = useState(null);

  const refreshSession = useCallback(async () => {
    setAuthReady(false);

    let nextMerchant = null;
    let nextShopper = null;

    const merchantToken = getToken();
    const shopperToken = getShopperToken();
    const veyaTeamToken = getVeyaTeamToken();

    if (merchantToken) {
      try {
        nextMerchant = await getCurrentUser();
      } catch {
        clearToken();
      }
    }

    if (shopperToken) {
      try {
        nextShopper = await getCurrentShopper();
      } catch {
        clearShopperToken();
      }
    }

    let nextVeyaTeam = null;

    if (veyaTeamToken) {
      // For now we trust token presence (no /me endpoint yet)
      nextVeyaTeam = { active: true };
    }

    setMerchant(nextMerchant);
    setShopper(nextShopper);
    setVeyaTeam(nextVeyaTeam);
    setAuthReady(true);
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const logoutMerchant = useCallback(() => {
    clearToken();
    setMerchant(null);
  }, []);

  const logoutShopper = useCallback(() => {
    clearShopperToken();
    setShopper(null);
  }, []);

  const logoutAll = useCallback(() => {
    clearToken();
    clearShopperToken();
    clearVeyaTeamToken();
    setMerchant(null);
    setShopper(null);
    setVeyaTeam(null);
  }, []);

const sessionType = veyaTeam
  ? "veya_team"
  : merchant
  ? "merchant"
  : shopper
  ? "shopper"
  : "anonymous";

  const isAuthenticated = sessionType !== "anonymous";

  const value = useMemo(
    () => ({
      authReady,
      sessionType,
      merchant,
      shopper,
      isAuthenticated,
      isMerchant: sessionType === "merchant",
      isShopper: sessionType === "shopper",
      isAnonymous: sessionType === "anonymous",
      isVeyaTeam: sessionType === "veya_team",
      refreshSession,
      logoutMerchant,
      logoutShopper,
      logoutAll,
      logout: logoutAll,
    }),
    [
      authReady,
      sessionType,
      merchant,
      shopper,
      isAuthenticated,
      refreshSession,
      logoutMerchant,
      logoutShopper,
      logoutAll,
    ]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const ctx = useContext(SessionContext);

  if (!ctx) {
    throw new Error("useSessionContext must be used within a SessionProvider");
  }

  return ctx;
}